import React, { useEffect, useState, useMemo, useCallback } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { obtenerFacturasActivas, obtenerXMLFactura, timbrarFactura } from "../services/facturaService";
import PageHeader from "../components/PageHeader";
import DataTable from "../components/DataTable";
import Toast, { useToast } from "../components/Toast";

const formatDate = (d) => {
  if (!d) return "";
  try {
    return new Date(d).toLocaleDateString("es-MX", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    });
  } catch {
    return String(d).slice(0, 10);
  }
};
const formatCurrency = (n) =>
  (Number(n) || 0).toLocaleString("es-MX", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const currentYear = new Date().getFullYear();
const yearsOptions = Array.from({ length: 6 }, (_, i) => currentYear - i);

const MESES = [
  "Todos",
  "Enero",
  "Febrero",
  "Marzo",
  "Abril",
  "Mayo",
  "Junio",
  "Julio",
  "Agosto",
  "Septiembre",
  "Octubre",
  "Noviembre",
  "Diciembre",
];

export default function FacturaPage40() {
  const [facturas, setFacturas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const location = useLocation();
  const navigate = useNavigate();
  const empresaId = Number(localStorage.getItem("empresaId") || 1);
  const [downloadingKey, setDownloadingKey] = useState(null);
  const [timbrandoKey, setTimbrandoKey] = useState(null);
  const toast = useToast();

  const handleDownloadXML = useCallback(async (r) => {
    const key = `${r.serie}-${r.factura}`;
    setDownloadingKey(key);
    try {
      const xml = await obtenerXMLFactura({ serie: r.serie, factura: r.factura, empresa: empresaId });
      const blob = new Blob([xml], { type: "application/xml" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${key}.xml`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error(`No se encontró el XML para la factura ${key}.`);
    } finally {
      setDownloadingKey(null);
    }
  }, [empresaId]);

  const handleTimbrar = useCallback(async (r) => {
    const key = `${r.serie}-${r.factura}`;
    setTimbrandoKey(key);
    try {
      const res = await timbrarFactura({ serie: r.serie, factura: r.factura, empresa: empresaId });
      toast.success(`Factura ${key} timbrada. UUID: ${res.uuid}`);
      setFacturas(prev => prev.map(f =>
        f.serie === r.serie && f.factura === r.factura ? { ...f, tieneCFDI: true } : f
      ));
    } catch (err) {
      toast.error(err?.response?.data?.detail || err?.response?.data?.message || err.message || "Error al timbrar.");
    } finally {
      setTimbrandoKey(null);
    }
  }, [empresaId]);

  const currentMonth = String(new Date().getMonth() + 1);
  const [filters, setFilters] = useState({
    clienteClave: "",
    moneda: "TODAS",
    anio: String(currentYear),
    mes: currentMonth,
  });

  const onChangeFilter = (e) => {
    const { name, value } = e.target;
    setFilters((f) => ({ ...f, [name]: value }));
  };

  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        setLoading(true);
        const data = await obtenerFacturasActivas();
        if (!cancel) {
          setFacturas(data || []);
          setError(null);
        }
      } catch (e) {
        if (!cancel) {
          setError("Error al cargar las facturas activas.");
          setFacturas([]);
        }
      } finally {
        if (!cancel) setLoading(false);
      }
    })();
    return () => {
      cancel = true;
    };
  }, []);

  const filteredFacturas = useMemo(() => {
    let list = facturas;
    const { clienteClave, moneda, anio, mes } = filters;
    if (moneda !== "TODAS") list = list.filter((f) => f.moneda === moneda);
    if (clienteClave) {
      const term = clienteClave.toLowerCase();
      list = list.filter(
        (f) =>
          (f.claveCliente || "").toLowerCase().includes(term) ||
          (f.nombreCliente || "").toLowerCase().includes(term),
      );
    }
    if (anio && anio !== "TODOS") {
      list = list.filter((f) => {
        const d = new Date(f.fecha);
        return !isNaN(d) && d.getFullYear() === parseInt(anio, 10);
      });
    }
    if (mes && mes !== "TODOS") {
      list = list.filter((f) => {
        const d = new Date(f.fecha);
        return !isNaN(d) && d.getMonth() + 1 === parseInt(mes, 10);
      });
    }
    return list;
  }, [facturas, filters]);

  const totalSaldo = useMemo(
    () => filteredFacturas.reduce((sum, f) => sum + (Number(f.saldo) || 0), 0),
    [filteredFacturas],
  );

  const columns = [
    { key: "claveCliente", title: "Cliente", style: { width: 120 } },
    { key: "nombreCliente", title: "Nombre" },
    {
      key: "documento",
      title: "Documento",
      style: { width: 170 },
      render: (r) => {
        const key = `${r.serie}-${r.factura}`;
        const isDownloading = downloadingKey === key;
        return (
          <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
            <Link
              to={`/facturacion/detalle/${r.serie}/${r.factura}/${empresaId}`}
              state={{ background: location }}
              style={{ color: "var(--clr-primary)", fontWeight: "var(--fw-semibold)", textDecoration: "none" }}
            >
              {key}
            </Link>
            {r.tieneCFDI ? (
              <span title="Timbrada ante el SAT" style={{
                fontSize: 10, fontWeight: 700, padding: "2px 7px", borderRadius: 99,
                background: "#dcfce7", color: "#15803d", border: "1px solid #bbf7d0",
                letterSpacing: 0.3,
              }}>✓ TIMBRADA</span>
            ) : (
              <span title="Sin timbrar" style={{
                fontSize: 10, fontWeight: 700, padding: "2px 7px", borderRadius: 99,
                background: "#fff7ed", color: "#c2410c", border: "1px solid #fed7aa",
                letterSpacing: 0.3,
              }}>⚠ SIN TIMBRAR</span>
            )}
            {r.tieneCFDI ? (
              <button onClick={() => handleDownloadXML(r)} disabled={isDownloading}
                title="Descargar XML"
                style={{ background: "none", border: "none", cursor: isDownloading ? "wait" : "pointer",
                  padding: "2px 4px", color: isDownloading ? "var(--clr-text-muted)" : "var(--clr-primary)",
                  lineHeight: 1, fontSize: 14, display: "flex", alignItems: "center" }}>
                {isDownloading ? "⏳" : "⬇️"}
              </button>
            ) : (
              <>
                <button onClick={() => handleTimbrar(r)} disabled={timbrandoKey === key}
                  title="Timbrar ante el SAT"
                  style={{ fontSize: 11, fontWeight: 700, padding: "3px 10px", borderRadius: 4,
                    background: timbrandoKey === key ? "#e5e7eb" : "#1a3e72", color: timbrandoKey === key ? "#6b7280" : "#fff",
                    border: "none", cursor: timbrandoKey === key ? "wait" : "pointer" }}>
                  {timbrandoKey === key ? "Timbrando..." : "Timbrar"}
                </button>
                <button onClick={() => navigate(`/facturacion/editar/${r.serie}/${r.factura}/${empresaId}`)}
                  title="Editar factura"
                  style={{ fontSize: 11, fontWeight: 700, padding: "3px 10px", borderRadius: 4,
                    background: "#f3f4f6", color: "#374151", border: "1px solid #d1d5db", cursor: "pointer" }}>
                  Editar
                </button>
              </>
            )}
          </div>
        );
      },
    },
    {
      key: "fecha",
      title: "Fecha",
      style: { width: 120 },
      render: (r) => formatDate(r.fecha),
    },
    {
      key: "saldo",
      title: "Saldo",
      style: { width: 140 },
      className: "num",
      render: (r) => formatCurrency(r.saldo),
    },
    {
      key: "moneda",
      title: "Moneda",
      style: { width: 90 },
      className: "center",
      render: (r) => (
        <span
          className={`lmd-badge ${r.moneda === "D" ? "lmd-badge-primary" : "lmd-badge-neutral"}`}
        >
          {r.moneda === "D" ? "USD" : "MXN"}
        </span>
      ),
    },
  ];

  return (
    <>
      <Toast toasts={toast.toasts} onClose={toast.closeToast} />
      <PageHeader
        title="Facturacion 4.0"
        subtitle="Consulta de facturas activas"
      />

      {/* Filtros */}
      <div className="lmd-card" style={{ marginBottom: "var(--sp-4)" }}>
        <div className="lmd-card-header">
          <h3 className="lmd-card-title">Filtros</h3>
          <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
            <span
              style={{
                fontSize: "var(--fs-sm)",
                color: "var(--clr-text-muted)",
              }}
            >
              {filteredFacturas.length} facturas
            </span>
            <span
              style={{
                fontWeight: "var(--fw-bold)",
                color:
                  totalSaldo > 0
                    ? "var(--clr-danger)"
                    : "var(--clr-text-primary)",
              }}
            >
              Total: {formatCurrency(totalSaldo)}
            </span>
          </div>
        </div>
        <div className="lmd-card-body">
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
              gap: "var(--sp-4)",
            }}
          >
            <div className="lmd-field">
              <label className="lmd-label">Cliente</label>
              <input
                className="lmd-input"
                name="clienteClave"
                value={filters.clienteClave}
                onChange={onChangeFilter}
                placeholder="Clave o nombre..."
              />
            </div>
            <div className="lmd-field">
              <label className="lmd-label">Moneda</label>
              <select
                className="lmd-select"
                name="moneda"
                value={filters.moneda}
                onChange={onChangeFilter}
                style={{ width: "100%" }}
              >
                <option value="TODAS">Todas</option>
                <option value="P">MXN</option>
                <option value="D">USD</option>
              </select>
            </div>
            <div className="lmd-field">
              <label className="lmd-label">Año</label>
              <select
                className="lmd-select"
                name="anio"
                value={filters.anio}
                onChange={onChangeFilter}
                style={{ width: "100%" }}
              >
                <option value="TODOS">Todos</option>
                {yearsOptions.map((y) => (
                  <option key={y} value={y}>
                    {y}
                  </option>
                ))}
              </select>
            </div>
            <div className="lmd-field">
              <label className="lmd-label">Mes</label>
              <select
                className="lmd-select"
                name="mes"
                value={filters.mes}
                onChange={onChangeFilter}
                style={{ width: "100%" }}
              >
                <option value="TODOS">Todos</option>
                {MESES.slice(1).map((m, i) => (
                  <option key={i + 1} value={i + 1}>
                    {m}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Tabla */}
      <div className="lmd-card">
        {loading && (
          <div className="lmd-loading-bar">
            <div className="lmd-spinner" />
            Cargando facturas...
          </div>
        )}
        {error && (
          <div
            style={{
              padding: "16px 20px",
              color: "var(--clr-danger)",
              background: "var(--clr-danger-bg)",
            }}
          >
            {error}
          </div>
        )}
        {!loading && !error && (
          <DataTable
            columns={columns}
            rows={filteredFacturas}
            keyField="factura"
          />
        )}
      </div>
    </>
  );
}
